import { DOMParser, XMLSerializer } from '@xmldom/xmldom';
import constants from './../../Constants';
import {
    isMedia,
    getMediaTargetId,
    getMediaPlaceholderType,
    generateFileUrlForLogicalName,
    isNewFormatImage,
} from './MediaModels';
import { isPDFPanel } from './PDFPanelModel';
import { isRubricBlock } from './RubricBlockModel';
import themeServices from '../../../themes/ThemeServices';
import { t } from '../../../services/i18nService';
import marketplacePropertiesService from '../../../services/MarketplacePropertiesService';
import objectTypes from '../../../constants/ObjectTypes';
import customFieldsService from '../../../services/CustomFieldsService';
import xmlDomUtils from '../../utils/XmlDomUtils';
import storeManager from '../../../store/StoreManager';
import sessionReader from '../../../store/readHelpers/SessionReader';
import settingsReader from '../../../store/readHelpers/SettingsReader';
import scorableItemNumberingService from '../../../services/questionView/ScorableItemNumberingService';
import uiService from '../../../services/UIService';

const serializer = new XMLSerializer();
export const INLINE_INTERACTIONS = ['inlineChoiceInteraction', 'textEntryInteraction'];

/**
 * Updates the itemBody element replacing the src attributes in "static" img elements to be treated as plain HTML
 * @param {Element} itemBody
 * @param assessmentRunId - optional
 */
export function replaceImageUrlsForImgTags(itemBody, assessmentRunId) {
    const imgTags = itemBody.getElementsByTagName('img');
    for (const imgTag of Array.from(imgTags)) {
        const logicalName = imgTag.getAttribute('data-logicalname');
        if (logicalName) {
            const newSrcUrl = generateFileUrlForLogicalName(logicalName, undefined, assessmentRunId, 'image');
            imgTag.setAttribute('src', newSrcUrl);
        }
    }
}

/**
 * Generates an id for a placeholder element based on question and response identifiers.
 *
 * @param {string} questionIdentifier
 * @param {string} responseIdentifier
 * @returns {string}
 */
export function getPlaceholderId(questionIdentifier, responseIdentifier) {
    return `${questionIdentifier}_${responseIdentifier}`;
}

/**
 * Checks if an interaction element is of a type meant to be displayed inline.
 *
 * @param {Element} interactionElement
 * @returns {boolean}
 */
export function isInlineInteraction(interactionElement) {
    return INLINE_INTERACTIONS.indexOf(interactionElement.localName) > -1;
}

export function createPlaceholderElement(interactionElement, questionIdentifier, forceDiv = false) {
    const responseIdentifier = interactionElement.getAttribute('responseIdentifier');
    const nodeType = !forceDiv && isInlineInteraction(interactionElement) ? 'span' : 'div';
    const placeholder = interactionElement.ownerDocument.createElement(nodeType);
    placeholder.textContent = 'placeholder';
    placeholder.setAttribute('id', getPlaceholderId(questionIdentifier, responseIdentifier));
    placeholder.setAttribute('class', 'interaction-container');

    return placeholder;
}

/**
 * Replaces an interaction element with a place holder element with questionIdentifier as id.
 *
 * @param {Element} interactionElement
 * @param {string} questionIdentifier
 */
export function replaceInteractionWithPlaceholder(interactionElement, questionIdentifier) {
    interactionElement.parentNode.replaceChild(
        createPlaceholderElement(interactionElement, questionIdentifier),
        interactionElement
    );
}

/**
 * Replaces a media element with a place holder element with logicalName as id.
 *
 * @param {Element} mediaElement
 * @param {string} questionIdentifier
 */
export function replaceMediaWithPlaceholder(mediaElement, questionIdentifier) {
    const doc = mediaElement.ownerDocument;
    const nodeType = getMediaPlaceholderType(mediaElement);

    let wrappedChildNodes = null;

    if (isNewFormatImage(mediaElement) && mediaElement.childNodes?.length) {
        // IMPORTANT: clone nodes BEFORE removing mediaElement
        wrappedChildNodes = Array.from(mediaElement.childNodes)
            .map(node => node.cloneNode(true));
    }

    const placeholder = doc.createElement(nodeType);
    placeholder.textContent = 'placeholder';
    placeholder.setAttribute(
        'id',
        getMediaTargetId(questionIdentifier, mediaElement)
    );
    placeholder.setAttribute('class', 'qti-renderer-media-wrapper');

    const parentNode = mediaElement.parentNode;
    if (!parentNode) {
        return;
    }

    parentNode.replaceChild(placeholder, mediaElement);

    // Insert wrongly wrapped content immediately AFTER placeholder
    if (wrappedChildNodes?.length) {
        const referenceNode = placeholder.nextSibling;

        wrappedChildNodes.forEach((node) => {
            parentNode.insertBefore(node, referenceNode);
        });
    }
}

/**
 * Checks if a DOM Element is an interaction.
 *
 * @param {Element} element
 * @returns {boolean}
 */
export function isInteraction(element) {
    return element.nodeType === 1 && element.hasAttribute('responseIdentifier');
}

export function isLanguageControlledElementType(nodeName) {
    return constants.languageControlledTags.indexOf(nodeName) !== -1;
}

export function isMediaElement(element) {
    return element.nodeName === 'div' && element.getAttribute('class').includes('ia-media');
}

export function isInsperaNamespacedStyleBodyElement(element) {
    return !isInteraction(element) && element.nodeType === 1 && element.hasAttribute('inspera:style');
}

/**
 * Utility function to check if the element is in the desired language
 * @param {Element} element
 * @param {string} requestedLanguage
 * @param {string} defaultLanguage
 * @returns {boolean}
 */
export function isInCorrectLanguage(element, requestedLanguage, defaultLanguage) {
    if (!isLanguageControlledElementType(element.nodeName) && !isMediaElement(element)) {
        return true;
    }

    const elementLanguage = element.getAttribute('lang');
    const useDefaultLanguage = requestedLanguage === undefined || requestedLanguage === defaultLanguage;
    const isInDefaultLanguage = !elementLanguage || elementLanguage === defaultLanguage;

    return (elementLanguage === requestedLanguage) || (useDefaultLanguage && isInDefaultLanguage);
}

/**
 * Empty inline elements get closed by XMLSerializer and can cause wrapping
 * of the following html when inserted into the DOM using dangerouslyInsertInnerHtml
 * @param {Element} element
 * @return {boolean}
 */
export function isInvalidEmptyInlineElement(element) {
    return element.nodeType === 1
        && constants.inlineElementsThatCannotBeEmpty.indexOf(element.localName) > -1
        && (!element.firstChild || isInvalidEmptyInlineElement(element.firstChild));
}

/**
 * The editor sometimes leaves damaged interactions in the html...
 * @param {Element} element
 * @return {boolean}
 */
export function isInvalidHtmlMarkup(element) {
    return !!(
        element.hasAttribute('placeholder-object-id')
        && element.localName.indexOf('interaction') !== -1
    );
}

export function removeHiddenStyle(element) {
    const styleStr = element.getAttribute('style') || '';
    if (!styleStr) {
        return undefined;
    }

    const replaced = styleStr
        .replace(/display:(\s)?none;/, '')
        .trim();
    if (replaced.length === 0) {
        return element.removeAttribute('style');
    }

    return element.setAttribute('style', replaced);
}

export function applyInsperaNamespaceStyle(element) {
    const insperaNamespaceStyle = element.getAttribute('inspera:style');
    element.removeAttribute('inspera:style');
    element.setAttribute('style', insperaNamespaceStyle);
    return element;
}

/**
 * Recursively loops through the elements in itemBody to look for interactions to replace and non matching languages to remove
 *
 * @param {Element} parentElement
 * @param {string} questionIdentifier
 * @param {string} language
 * @param {string} defaultLanguage
 */
export function getComponentNodes(parentElement, questionIdentifier, language, defaultLanguage) {
    Array.from(parentElement.childNodes).forEach((node) => {
        if (node.nodeType !== 1) {
            // non elements should be ignored
            return undefined;
        } else if (isInteraction(node)) {
            // interactions should be replaced by placeholders to be used as targets in the renderer
            return replaceInteractionWithPlaceholder(node, questionIdentifier);
        } else if (isPDFPanel(node)) {
            return parentElement.removeChild(node);
        } else if (isRubricBlock(node)) {
            return parentElement.removeChild(node);
        } else if (isMedia(node)) {
            // media elements should be replaced by placeholders to be used as targets in the renderer
            return replaceMediaWithPlaceholder(node, questionIdentifier);
        } else if (isInvalidEmptyInlineElement(node)) {
            // remove inline elements that are empty as they can wrap content
            return parentElement.removeChild(node);
        } else if (isInvalidHtmlMarkup(node)) {
            // remove blocks that cause harm like damaged interaction leftovers
            return parentElement.removeChild(node);
        } else if (!isInCorrectLanguage(node, language, defaultLanguage)) {
            // block elements in wrong language should be removed
            parentElement.removeChild(node);
            if (isInvalidEmptyInlineElement(parentElement)) {
                // after child removal, its parent might become empty. removing the parent if so.
                // asking his grandfather to do it ...
                parentElement.parentNode.removeChild(parentElement);
            }
            return undefined;
        }

        if (node.hasAttribute('lang') && node.hasAttribute('style')) {
            removeHiddenStyle(node);
        }

        if (isInsperaNamespacedStyleBodyElement(node)) {
            applyInsperaNamespaceStyle(node);
        }

        if (parentElement.hasChildNodes()) {
            return getComponentNodes(node, questionIdentifier, language, defaultLanguage);
        }

        return undefined;
    });
}

export function getInteractionElements(parentElement, language, defaultLanguage) {
    return Array
        .from(parentElement.childNodes)
        .reduce((accumulator, node) => {
            if (node.nodeType !== 1) {
                return accumulator;
            }

            if (isInteraction(node)) {
                accumulator.push(node);
                return accumulator;
            }

            if (isInCorrectLanguage(node, language, defaultLanguage)) {
                const elms = getInteractionElements(node);
                if (elms.length) {
                    accumulator.push(...elms);
                }
            }
            return accumulator;
        }, []);
}

export function htmlFromPlaceholderElement(element) {
    return serializer.serializeToString(element);
}

export function placeholderElementMapper(questionIdentifier) {
    return interactionElement => createPlaceholderElement(interactionElement, questionIdentifier, true);
}

export function getInteractionElementsAsHtml(parentElement, questionIdentifier, language, defaultLanguage) {
    return getInteractionElements(parentElement, language, defaultLanguage)
        .map(placeholderElementMapper(questionIdentifier))
        .map(htmlFromPlaceholderElement)
        .join('\n');
}

export function isValidFlowChartItemType(data) {
    const validFlowChartItemTypes = [
        'Flow-chart Completion Productive',
        'Flow-chart Completion Objective',
    ];

    // Ensure data is an array, if not, convert it to an array
    const dataArray = Array.isArray(data) ? data : [data];

    for (const field of dataArray) {
        if (field?.name === 'Item Type') {
            const itemTypes = Array.isArray(field.value) ? field.value : [field.value];
            return itemTypes.some(itemType => validFlowChartItemTypes.includes(itemType));
        }
    }
    return false;
}

export function replaceNextSibling(nextSiblingData) {
    if (!nextSiblingData?.trim()) return null;

    return nextSiblingData
        .replace(/\n/g, '')
        .replace(/\n(\S)/g, (_match, p1) => p1.trim());
}

export function replaceSpaceAfterInteraction(node) {
    if (node.nodeType === Node.TEXT_NODE) return;

    const interactionElement = node.getElementsByTagName('span');
    const nodesWithId = Array.from(interactionElement).filter(element => element.getAttribute('id'));

    if (!nodesWithId.length) return;

    nodesWithId.forEach(element => removeNextSiblingSpace(element));
}

function removeNextSiblingSpace(interactionElement) {
    const updatedSiblingData = replaceNextSibling(interactionElement?.nextSibling?.data);

    if (typeof updatedSiblingData === 'string') {
        interactionElement.nextSibling.data = updatedSiblingData;
    }
}

/**
 * Returns a light weight html from the itemBodyElement for the language provided. Content on other languages will be skipped.
 * The interactions will be replaced by place holders.
 * @param {string} questionIdentifier
 * @param {Element} itemBody
 * @param {string} language
 * @param {boolean} parseInteractionsOnly - optional
 * @param {object} qtiData
 * @returns {string}
 */
function hasEssayQuestionsInSection(qtiData) {
    const questions = getQuestionsFromSection(qtiData);

    return questions?.some(question =>
        [objectTypes.EXTENDED_TEXT, objectTypes.EXTENDED_TEXT_MATH].includes(question.objectType)
    ) || false;
}

/**
 * Finds the question number for a stimulus by matching contentItemId and contentRevisionId
 * Only returns a number if the question has an essay objectType
 * @param {object} qtiData
 * @returns {number|null}
 */
function getQuestionNumberForEssayWithStimulus(qtiData) {
    if (!qtiData || !qtiData.contentItemId || !qtiData.contentRevisionId) {
        return null;
    }

    const state = storeManager.getState();

    // Search through all sections and questions to find a stimulus that matches
    for (const section of state.assessment?.sections || []) {
        for (const question of section.questions || []) {
            const stimulus = question.stimulus;

            // Check if this question's stimulus matches the qtiData and has essay objectType
            if (stimulus &&
                stimulus.contentItemId === qtiData.contentItemId &&
                stimulus.contentRevisionId === qtiData.contentRevisionId &&
                [objectTypes.EXTENDED_TEXT, objectTypes.EXTENDED_TEXT_MATH].includes(question.objectType)) {
                const orderNumberOfEssayQuestion = scorableItemNumberingService.getFirstValidOrderNumberInQuestion(
                    question.contentRevisionId
                );
                const questionNumber = orderNumberOfEssayQuestion || question.questionNumberInQuestionSet;
                const interactionNumber = Number.parseInt(questionNumber, 10);
                return Number.isNaN(interactionNumber) ? 1 : interactionNumber;
            }
        }
    }

    return null;
}

function getStimulusSettings(qtiData, questionIdentifier) {
    let interactionOrSectionNumber;
    if (uiService.isPbAndMultipleStimulusEnabled() && qtiData?.sectionNumber) {
        const questions = getQuestionsFromSection(qtiData);
        const questionWithStimulus = questions?.find(question => question?.stimulus?.contentRevisionId === questionIdentifier);
        interactionOrSectionNumber = questionWithStimulus?.questionNumberingInSection;
    }

    const questionNumber = getQuestionNumberForEssayWithStimulus(qtiData);
    if (questionNumber) {
        return {
            marginClass: ' ml-2em',
            bodyString: `<p class="interaction-number">${questionNumber} </p>`,
        };
    }

    if (!interactionOrSectionNumber || qtiData.sectionNumber || !hasEssayQuestionsInSection(qtiData)) {
        return { marginClass: '', bodyString: '' };
    }

    return {
        marginClass: ' ml-2em',
        bodyString: `<p class="interaction-number">${interactionOrSectionNumber} </p>`,
    };
}

function getQuestionsFromSection(qtiData) {
    const state = storeManager.getState();
    const sectionIndex = qtiData?.sectionNumber ? qtiData.sectionNumber - 1 : 0;
    const questions = state.assessment?.sections?.[sectionIndex]?.questions;
    return questions;
}

function getTextEntrySettings(qtiData, responseModels) {
    if (!qtiData || !responseModels || responseModels?.length > 1) {
        return { marginClass: '', bodyString: '' };
    }

    const interactionNumber = scorableItemNumberingService.getInteractionNumber(qtiData.contentRevisionId, responseModels[0].identifier);
    if (!interactionNumber) {
        return { marginClass: '', bodyString: '' };
    }

    return {
        marginClass: ' ml-2em',
        bodyString: `<p class="interaction-number">${interactionNumber} </p>`,
    };
}

function getPaperBasedSettings(isPaperBased, isPBRenderStyleEnabled, questionType, qtiData, responseModels, questionIdentifier) {
    if (!isPaperBased || !isPBRenderStyleEnabled) {
        return { marginClass: '' };
    }

    const isStimulus = (questionType === objectTypes.STIMULUS
        || questionType === objectTypes.CONTENT_BLOCK
        || qtiData?.category === objectTypes.CATEGORY.STIMULUS
    );
    const isTextEntry = questionType === objectTypes.TEXT_ENTRY;

    if (isStimulus) {
        return getStimulusSettings(qtiData, questionIdentifier);
    }

    if (isTextEntry) {
        return getTextEntrySettings(qtiData, responseModels);
    }

    return { marginClass: '' };
}

export function getItemBodyAsHTML(
    questionIdentifier,
    itemBody,
    language,
    parseInteractionsOnly,
    questionType,
    itemTypeDetails,
    extraParams,
) {
    const defaultLanguage = itemBody.getAttribute(constants.defaultLanguageAttr);
    const supportedLanguages = itemBody.getAttribute(constants.supportedLanguagesAttr);
    const languageIsSupported = supportedLanguages.includes(language);
    const langToUse = languageIsSupported ? language : undefined;
    const { qtiData, responseModels } = extraParams || {};
    // avoid having itemBody element as the root tag
    let bodyString = '';

    const isAudioScriptQuestion = questionType === objectTypes.AUDIO_SCRIPT;
    const isBorderHidden = (isAudioScriptQuestion && marketplacePropertiesService.shouldHideGridLinesInAudioScript());
    const showGridLines = themeServices.isCeq() && isAudioScriptQuestion && !marketplacePropertiesService.shouldHideGridLinesInAudioScript();
    const hasValidFlowChartItemType = isValidFlowChartItemType(itemTypeDetails);
    // Add a extra class if hasValidFlowChartItemType is true
    const extraClass = hasValidFlowChartItemType && marketplacePropertiesService.isEnableLeftAlignFlowChartItemType()
        ? ' flow-chart-item'
        : '';

    if (parseInteractionsOnly) {
        bodyString = getInteractionElementsAsHtml(itemBody, questionIdentifier, langToUse, defaultLanguage);
    } else {
        getComponentNodes(itemBody, questionIdentifier, langToUse, defaultLanguage);

        bodyString = Array
            .from(itemBody.childNodes)
            .reduce((str, node) => {
                replaceSpaceAfterInteraction(node);
                return str.concat(serializer.serializeToString(node));
            }, '');
    }

    const isPaperBased = sessionReader.isPrintStylePaperBased();
    const isPBRenderStyleEnabled = settingsReader.isPBRenderStyleEnabled();
    const paperBasedSettings = getPaperBasedSettings(isPaperBased, isPBRenderStyleEnabled, questionType, qtiData, responseModels, questionIdentifier);
    const marginClass = paperBasedSettings.marginClass;
    bodyString = (paperBasedSettings.bodyString || '') + bodyString;
    const isShortAnswerQuestion = qtiData ? customFieldsService.isShortAnswerQuestion(qtiData) : null;
    const removeBorderClass = `${extraClass}${isBorderHidden ? ' remove_border' : ''}`.trim();
    const showGridClass = showGridLines ? ' grid-lines' : '';
    const divClass = `${removeBorderClass}${showGridClass}${marginClass}${isShortAnswerQuestion ? ' shortAnswerQuestionType' : ''}`.trim();
    // Ensure a space between `id` and `class` attributes
    const classAttribute = divClass ? ` class="${divClass}"` : '';

    return `<div id="interactionWrapper_${questionIdentifier}"${classAttribute}>${bodyString}</div>`;
}

export function replaceSpaceAfterPrintedVariable(variable, isCalledFromFeedbackContext) {
    const parentNode = variable?.parentNode;
    if (!parentNode?.nextSibling) return;

    const isMathTex = xmlDomUtils.hasClass(parentNode, 'math-tex');
    const node = isMathTex ? parentNode : variable;
    const updatedSibling = replaceNextSibling(node.nextSibling?.nodeValue);

    if (!isCalledFromFeedbackContext && typeof updatedSibling === 'string') {
        node.nextSibling.data = updatedSibling;
    }
}

/**
 * Extracts the question text from the itemBody element.
 * This function returns the text content of all <p> tags in the itemBody,
 * excluding interaction elements and elements in incorrect language.
 *
 * @param {Element|string} itemBody - The itemBody element or XML string containing the question text
 * @param {string} language - The language to use
 * @param {string} defaultLanguage - The default language
 * @returns {string} - The question text as HTML
 */
export function getQuestionTextFromItemBody(itemBody, language, defaultLanguage) {
    let itemBodyElement;
    if (typeof itemBody === 'string') {
        const domParser = new DOMParser();
        itemBodyElement = domParser.parseFromString(itemBody, 'text/xml').firstChild;
    } else {
        itemBodyElement = itemBody;
    }

    const itemBodyClone = itemBodyElement.cloneNode(true);
    const langToUse = language || defaultLanguage;
    const blockInteractions = Array.from(itemBodyClone.getElementsByTagName('p'))
        .filter(p => p.getAttribute('class') === 'blockInteraction');

    if (blockInteractions.length > 0) {
        blockInteractions.forEach((p) => {
            if (p.parentNode) p.parentNode.removeChild(p);
        });
    }

    const interactionElements = Array.from(itemBodyClone.getElementsByTagName('*'))
        .filter(element => isInteraction(element));

    interactionElements.forEach((element) => {
        if (element.parentNode) {
            element.parentNode.removeChild(element);
        }
    });

    const paragraphs = Array.from(itemBodyClone.getElementsByTagName('p'))
        .filter(element => isInCorrectLanguage(element, langToUse, defaultLanguage));

    const paragraphsHtml = paragraphs.map(p => serializer.serializeToString(p)).join('');

    return paragraphsHtml;
}

export function replacePrintedVariables(
    itemBody,
    templateDeclarations,
    qtiData,
    isCalledFromFeedbackContext = false
) {
    const printedVariables = itemBody.getElementsByTagName('printedVariable');
    const domParser = new DOMParser();
    const missingIdentifier = '[MISSING_IDENTIFIER]';

    Array.from(printedVariables).forEach((variable) => {
        const identifier = variable.getAttribute('identifier');
        const className = variable.getAttribute('class');
        let template;

        if (className?.includes('system-variable')) {
            template = themeServices.getThemeInstance().getSystemPrintedVariable(identifier, qtiData);
        } else if (identifier) {
            const templateDeclaration = templateDeclarations.find(item => item.identifier === identifier);
            const effectiveValues = templateDeclaration?.value || templateDeclaration?.defaultValue;
            const notReplacedVariable = isCalledFromFeedbackContext ?
                t('questionFeedback.feedbackBody.unansweredInteraction') :
                `[${identifier}]`;
            template = (Array.isArray(effectiveValues) && effectiveValues.join('')) || notReplacedVariable;
        } else {
            template = missingIdentifier;
        }

        let parsedDom = domParser.parseFromString(template, 'text/xml');
        const containsATagButNotStartingWithOne = template.includes('</') && !template.trim().startsWith('<');
        if (containsATagButNotStartingWithOne || parsedDom?.childNodes?.length > 1) {
            // if not starting with a tag or more than one child, the template should be wrapped to make sure no data is missed
            parsedDom = domParser.parseFromString(`<span>${template}</span>`, 'text/xml');
        }
        const parsedTemplate = parsedDom?.firstChild;

        if (!parsedTemplate) {
            // in test mode DOMParser returns undefined if string is empty or not defined
            // just remove printedVariable if not parsable
            variable.parentNode.removeChild(variable);
            return;
        }

        replaceSpaceAfterPrintedVariable(variable, isCalledFromFeedbackContext);
        variable.parentNode.replaceChild(parsedTemplate, variable);
    });
}
